import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Calendar, Eye, Monitor } from "lucide-react";
import { Button } from "@shared/ui/button";
import { ConnectionsFilter } from "./ConnectionsFilter";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useGetResourceUnitAssignments } from "@features/onboarding/utility-setup/hooks";
import { onboardingApi } from "@features/onboarding/utility-setup/api";

export interface MeterRecord {
  id: string;
  meterId: string;
  service: string;
  serialNo: string;
  type: string;
  subType: string;
  lastReading: string;
  readDate: string;
  installDate: string;
  resourceTypeDisplay: string;
}

export const INITIAL_METERS_DATA: MeterRecord[] = [];

const getServiceBadgeStyle = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-50 text-blue-600 border-blue-200/80";
    case "Electricity":
      return "bg-amber-50 text-amber-700 border-amber-200/80";
    case "Gas":
      return "bg-orange-50 text-orange-700 border-orange-200/80";
    case "Solar":
      return "bg-emerald-50 text-emerald-700 border-emerald-200/80";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

const getServiceDotColor = (service: string) => {
  switch (service) {
    case "Water":
      return "bg-blue-500";
    case "Electricity":
      return "bg-amber-500";
    case "Gas":
      return "bg-orange-500";
    case "Solar":
      return "bg-emerald-500";
    default:
      return "bg-gray-400";
  }
};

interface ConnectionsProps {
  meters?: MeterRecord[];
}

const Connections: React.FC<ConnectionsProps> = ({
  meters = INITIAL_METERS_DATA,
}) => {
  const navigate = useNavigate();
  const { code } = useParams<{ code?: string }>();
  const decodedUnitCode = decodeURIComponent(code);
  const remoteUtilityId = useRemoteUtilityId();

  const [filters, setFilters] = useState({
    search: "",
    utility_service: "",
    type: "",
  });

  const { data: assignmentsData, isLoading } = useGetResourceUnitAssignments({
    remoteUtilityId,
    unitCode: decodedUnitCode,
    searchData: filters.search || undefined,
    utilityService: filters.utility_service || undefined,
  });

  const form = useForm({
    defaultValues: {
      utility_service: "",
      type: "",
    },
  });

  const displayMeters: MeterRecord[] = useMemo(() => {
    const rawList = Array.isArray(assignmentsData?.result)
      ? assignmentsData.result
      : meters || [];

    if (rawList && rawList.length > 0) {
      return rawList.map(
        (item: any, index: number): MeterRecord => ({
          id: String(item.meterId),
          meterId: item.resourceId || "N/A",

          service: item.utilityService || "N/A",
          serialNo: item.serialNo || "N/A",
          type: item.resourceTypeName || "N/A",
          subType: item.resourceSubTypeName || "N/A",
          lastReading: item.lastReading,
          readDate: item.readDate || "N/A",
          installDate: item.installDate || "N/A",
          resourceTypeDisplay: item.resourceTypeDisplay || "N/A",
        }),
      );
    }

    return meters;
  }, [assignmentsData, meters]);

  const filteredData = useMemo(() => {
    return displayMeters.filter((item) => {
      // Type filter
      if (
        filters.type &&
        item.type.toLowerCase() !== filters.type.toLowerCase()
      ) {
        return false;
      }

      return true;
    });
  }, [displayMeters, filters.type]);

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm }));
  };

  const handleApplyFilters = (data: any) => {
    setFilters((prev) => ({
      ...prev,
      utility_service: data.utility_service || "",
      type: data.type || "",
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      utility_service: "",
      type: "",
    });
    form.reset({
      utility_service: "",
      type: "",
    });
  };

  const handleExport = async () => {
    try {
      const exportResponse = await onboardingApi.getResourceUnitAssignments({
        remoteUtilityId,
        unitCode: decodedUnitCode,
        searchData: filters.search || undefined,
        utilityService: filters.utility_service || undefined,
        isExport: true,
      });

      const exportList = Array.isArray(exportResponse)
        ? exportResponse
        : Array.isArray(exportResponse?.result)
          ? exportResponse.result
          : Array.isArray(exportResponse?.results)
            ? exportResponse.results
            : filteredData;

      const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(exportList, null, 2),
      )}`;
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", jsonString);
      downloadAnchor.setAttribute(
        "download",
        `resource_unit_assignments_${decodedUnitCode || "unit"}.json`,
      );
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    } catch {
      const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(filteredData, null, 2),
      )}`;
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", jsonString);
      downloadAnchor.setAttribute("download", "resource_unit_assignments.json");
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    }
  };

  const columns: TableColumn<MeterRecord>[] = useMemo(
    () => [
      {
        key: "meterId",
        header: "Resource ID",
      },
      {
        key: "service",
        header: "Service",
        render: (val) => (
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${getServiceBadgeStyle(
              val,
            )}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${getServiceDotColor(val)}`}
            />
            {val}
          </span>
        ),
      },
      {
        key: "serialNo",
        header: "Serial No.",
      },
      {
        key: "resourceTypeDisplay",
        header: "Type",
        type:"badge"
      },
      {
        key: "lastReading",
        header: "Last Reading",
        render: (val) => (
          <span className="text-xs font-bold text-gray-900">{val}</span>
        ),
      },
      {
        key: "readDate",
        header: "Read Date",
        render: (val) => (
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {val}
          </span>
        ),
      },
      {
        key: "installDate",
        header: "Install Date",
        render: (val) => (
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            {val}
          </span>
        ),
      },
    ],
    [],
  );

  const action: TableAction<any>[] = [
    {
      label: "View",
      icon: <Eye className="w-3.5 h-3.5 mr-1 text-gray-500" />,
      onClick: (_row: any) => {
        navigate(`/mx/manage/resources/view/${_row.id}`);
      },
    },
  ];

  return (
    <div className="pt-4">
      {/* Generic Table with filterPanel */}
      <GenericTable
        data={filteredData || []}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        onSearch={handleSearch}
        searchPlaceholder="Search meters..."
        onExport={handleExport}
        actions={action}
        actionsType="icons"
        filterPanel={
          <ConnectionsFilter
            form={form}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        }
      />
    </div>
  );
};

export default Connections;
